<template>
    <div class="product-search-table mb-lg" :class="{ 'product-search-table--end': reverseDirection === true }">
        <div ref="stickycolhead" class="product-search-table__sticky product-search-table__sticky--head" :style="{ width: cellWidth(tableReference.title) }">
            <table class="table">
                <thead>
                    <tr>
                        <th>{{ tableReference[Object.keys(tableReference)[0]].label }}</th>
                    </tr>
                </thead>
            </table>
        </div>

        <div ref="head" class="product-search-table__data product-search-table__data--head">
            <table ref="headtable" class="table">
                <col v-for="(value, key) in tableReference" :key="`col-${key}`" :width="cellWidth(value)">
                <thead>
                    <tr>
                        <th v-for="(value, key, index) in tableReference" :key="`head-${key}`" :class="{ 'first': index === 0, 'last': index === Object.keys(products[0].table).length - 1 }">{{ value.label }}</th>
                    </tr>
                </thead>
            </table>

            <button class="product-search-table__button" @click="handleClick()">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                    <path d="M5.5 0l-2 2 6 6-6 6 2 2 8-8-8-8z" />
                </svg>
            </button>
        </div>
        
        <div ref="stickycolbody" class="product-search-table__sticky product-search-table__sticky--body" :style="{ width: cellWidth(tableReference.title) }">
            <table class="table">
                <tbody>
                    <tr v-for="product in products" :key="product.objectID">
                        <td :style="{ 'max-width': cellWidth(tableReference.title) }">
                            <div>
                                <span>{{ product.title }}</span>
                                <Tooltip v-if="product.title" :text="product.title" position="center" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div ref="body" class="product-search-table__data product-search-table__data--body">
            <table ref="bodytable" class="table">
                <col v-for="(value, key) in tableReference" :key="`col-${key}`" :width="cellWidth(value)">

                <tbody>
                    <tr v-for="product in products" :key="product.objectID">
                        <td v-for="(value, key, index) in product.table" :key="`${product.objectID}-${key}`" :class="{ 'first': index === 0, 'last': index === Object.keys(tableReference).length - 1 }">
                            <div>
                                <span>{{ product[key] }}</span>
                                <Tooltip v-if="product[key]" :text="product[key]" position="center" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>    
        </div>
    </div>
</template>

<script>
import Headroom from 'headroom.js';
import { debounce, throttle } from 'lodash';
import Tooltip from '../components/Tooltip.vue';

export default {
    components: {
        Tooltip
    },

    props: {
        products: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            head: null,
            headtable: null,
            body: null,
            bodytable: null,
            stickycolhead: null,
            stickycolbody: null,
            allCells: [],
            visibleCells: [],
            windowWidth: null,
            reverseDirection: false,
            defaultWidth: 200,

            headroom: null,
            headroomsticky: null,
            isDesktop: null,
        };
    },

    computed: {
        tableReference() {
            return this.products[0].table;
        },

        fixedCell() {
            return this.allCells[0];
        }
    },

    watch: {
        visibleCells(cells) {
            const tableWidth = this.bodytable.offsetWidth;
            const viewableWidth = this.body.offsetWidth;
            let currentScroll = Math.abs(parseInt(this.bodytable.style.left));
            currentScroll = Number.isInteger(currentScroll) ? currentScroll : 0;
            
            // Calculate amount to scroll
            const toScroll = cells.reduce((total, cell) => {
                if (cell.target.classList.contains('last') && cells.length > 1) return total;
                return total + cell.target.getBoundingClientRect().width;
            }, 0);
            
            const totalScrollAfter = currentScroll + toScroll;

            if (totalScrollAfter + viewableWidth < tableWidth) {
                // Standard scroll
                this.headtable.style.left = `-${totalScrollAfter}px`;
                this.bodytable.style.left = `-${totalScrollAfter}px`;
            } else {
                const toRightEdge = tableWidth - viewableWidth;
                this.headtable.style.left = `-${toRightEdge}px`;
                this.bodytable.style.left = `-${toRightEdge}px`;
                this.bodytable.addEventListener('transitionend', this.atEnd);
            }
        },

        windowWidth() {
            const isDesktop = window.matchMedia('(min-width: 1024px)');
            this.isDesktop = isDesktop.matches;
            this.refreshTable();
        },

        products() {
            this.$nextTick(() => {
                this.allCells = this.bodytable.querySelectorAll('tr:first-child td');
                this.refreshTable();
            });
        }
    },

    mounted() {
        this.head = this.$refs.head;
        this.headtable = this.$refs.headtable;
        this.body = this.$refs.body;
        this.bodytable = this.$refs.bodytable;
        this.stickycolhead = this.$refs.stickycolhead;
        this.stickycolbody = this.$refs.stickycolbody;
        this.allCells = this.bodytable.querySelectorAll('tr:first-child td');

        this.headroomInit();
        this.checkOverflow();
        this.windowWidth = window.innerWidth;
        
        window.addEventListener('resize', debounce(() => {
            this.windowWidth = window.innerWidth;
            this.headroom.destroy();
            this.headroomsticky.destroy();
            this.headroomInit();
        }, 500));
    },

    updated() {
        this.checkOverflow();
    },

    methods: {
        headroomInit() {
            const tablestart = this.head.getBoundingClientRect().top + window.scrollY;
            this.headroom = new Headroom(this.head, { offset: tablestart });
            this.headroomsticky = new Headroom(this.stickycolhead, { offset: tablestart });

            this.headroom.init();
            this.headroomsticky.init();

            window.addEventListener('scroll', throttle(() => {
                if (tablestart + this.body.offsetHeight > window.pageYOffset) {
                    this.head.style.visibility = 'visible';
                    this.stickycolhead.style.visibility = 'visible';
                } else {
                    this.head.style.visibility = 'hidden';
                    this.stickycolhead.style.visibility = 'hidden';
                }
            }, 250));
        },

        cellWidth(cell) {
            return `${cell.width || this.defaultWidth}px`;
        },

        handleClick() {
            // If at end of table
            if (this.reverseDirection) {
                this.headtable.style.left = '0px';
                this.bodytable.style.left = '0px';
                this.bodytable.addEventListener('transitionend', this.atStart);
                return false;
            }

            // IntersectionObserver options
            const observerOffset = this.isDesktop ? `-${this.fixedCell.getBoundingClientRect().width}px` : '0px';
            const options = {
                root: this.body,
                rootMargin: `0px 0px 0px ${observerOffset}`,
                threshold: 1.0,
            };

            // IntersectionObserver
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    // Stop observing entry
                    observer.unobserve(entry.target);
                });

                this.visibleCells = entries.filter((entry) => {

                    // Stop observing entry
                    observer.unobserve(entry.target);

                    // If entry is intersecting at all
                    return entry.intersectionRatio > 0;

                }).filter((entry, index, array) => {

                    // If there are multiple intersecting cells, return fully intersected
                    if (array.length > 1) return entry.intersectionRatio > 0.99;

                    // Return single cell however intersected
                    return entry;

                });
            }, options);

            // Fire IntersectionObserver for each cell
            this.allCells.forEach((cell) => {
                observer.observe(cell);
            });

            // Exlicit return
            return undefined;
        },

        atStart() {
            this.bodytable.removeEventListener('transitionend', this.atStart);
            this.reverseDirection = false;
        },

        atEnd() {
            this.bodytable.removeEventListener('transitionend', this.atEnd);
            this.reverseDirection = true;
        },

        refreshTable() {
            // Get amount table has been scrolled by and process into a positive integer
            const alreadyScrolled = Math.abs(parseInt(this.bodytable.style.left, 10));
            
            // If not a number, stop.
            if (!alreadyScrolled) return false;

            // Get widths of elements
            const wrapWidth = this.body.getBoundingClientRect().width;
            const scrolledWidth = this.bodytable.getBoundingClientRect().width;

            // Get difference
            const difference = scrolledWidth - wrapWidth;

            // If difference is less than amount scrolled, stop.
            if (difference > alreadyScrolled) return false;

            // Update scroll left value
            this.headtable.style.left = `-${difference}px`;
            this.bodytable.style.left = `-${difference}px`;
            this.bodytable.addEventListener('transitionend', this.atEnd);
        },

        checkOverflow() {
            // Check if overflowed
            [...this.bodytable.querySelectorAll('td > div > span'),
            ...this.stickycolbody.querySelectorAll('td > div > span')].forEach((node) => {
                if (node.scrollWidth > node.offsetWidth) {
                    node.classList.add('overflowed');
                }
            });
        }
    }
}
</script>
